import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
import { getMarketplaceStateConfig } from "config/workflowStates";

const SECTOR_ICONS = {
  Energy: "Zap",
  Transport: "Train",
  "Digital Infrastructure": "Wifi",
  Water: "Droplets",
};

const RISK_COLORS = {
  Low: { bg: "rgba(5,150,105,0.1)", text: "var(--color-success)" },
  Medium: { bg: "rgba(217,119,6,0.1)", text: "var(--color-warning)" },
  High: { bg: "rgba(220,38,38,0.1)", text: "var(--color-error)" },
};

const STAGE_COLORS = {
  Sourcing: { bg: "rgba(107,114,128,0.1)", text: "var(--color-muted-foreground)" },
  Screening: { bg: "rgba(59,110,165,0.1)", text: "var(--color-primary)" },
  Diligence: { bg: "rgba(59,76,76,0.15)", text: "#92600A" },
  "IC Review": { bg: "rgba(139,92,246,0.1)", text: "#7C3AED" },
  Execution: { bg: "rgba(5,150,105,0.1)", text: "var(--color-success)" },
  "Asset Management": { bg: "rgba(59,76,76,0.1)", text: "var(--color-primary)" },
};

export default function OpportunityCard({ opportunity, isBookmarked, isExternal, onBookmarkToggle, stateOverrides = {}, onRequestAccess }) {
  const navigate = useNavigate();
  const riskStyle   = RISK_COLORS?.[opportunity?.riskRating] || RISK_COLORS?.Medium;
  const stageStyle  = STAGE_COLORS?.[opportunity?.stage] || STAGE_COLORS?.Sourcing;
  const activeState = stateOverrides?.[opportunity?.id] || opportunity?.marketplaceState;
  const mktCfg      = activeState ? getMarketplaceStateConfig(activeState) : null;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs hover:shadow-md transition-all duration-250 hover-lift card-interactive flex flex-col">
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <Image
          src={opportunity?.image}
          alt={opportunity?.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {/* Sector badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm">
          <Icon name={SECTOR_ICONS?.[opportunity?.sector] || "Building2"} size={13} color="var(--color-primary)" />
          <span className="text-xs font-caption font-semibold text-primary">{opportunity?.sector}</span>
        </div>
        {/* Bookmark */}
        <button
          onClick={(e) => { e?.stopPropagation(); onBookmarkToggle(opportunity?.id); }}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
          aria-label={isBookmarked ? "Remove bookmark" : "Bookmark opportunity"}
        >
          <Icon
            name={isBookmarked ? "Bookmark" : "BookmarkPlus"}
            size={15}
            color={isBookmarked ? "var(--color-primary)" : "var(--color-muted-foreground)"}
            strokeWidth={isBookmarked ? 2.5 : 2}
          />
        </button>
        {/* Stage */}
        <div
          className="absolute bottom-3 left-3 px-2 py-0.5 rounded text-xs font-caption font-medium"
          style={{ background: stageStyle?.bg, color: stageStyle?.text, backdropFilter: "blur(4px)" }}
        >
          {opportunity?.stage}
        </div>
        {/* Marketplace access state badge — shown to external investors */}
        {mktCfg && isExternal && (
          <div
            className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-caption font-semibold"
            style={{ background: mktCfg.bgColor, color: mktCfg.color, backdropFilter: "blur(4px)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: mktCfg.color }} />
            {mktCfg.label}
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <div className="mb-3">
          <h3 className="font-heading font-semibold text-foreground text-base leading-snug line-clamp-2 mb-1">
            {opportunity?.name}
          </h3>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Icon name="MapPin" size={13} />
            <span className="text-xs font-caption">{opportunity?.region}</span>
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-muted rounded-lg p-2.5">
            <p className="text-xs font-caption text-muted-foreground mb-0.5">Target IRR</p>
            <p className="text-sm font-data font-semibold text-foreground">{opportunity?.targetIRR}%</p>
          </div>
          <div className="bg-muted rounded-lg p-2.5">
            <p className="text-xs font-caption text-muted-foreground mb-0.5">Ticket Size</p>
            <p className="text-sm font-data font-semibold text-foreground">{opportunity?.ticketSize}</p>
          </div>
          <div className="bg-muted rounded-lg p-2.5">
            <p className="text-xs font-caption text-muted-foreground mb-0.5">Risk Rating</p>
            <p className="text-sm font-caption font-semibold" style={{ color: riskStyle?.text }}>{opportunity?.riskRating}</p>
          </div>
          <div className="bg-muted rounded-lg p-2.5">
            <p className="text-xs font-caption text-muted-foreground mb-0.5">Equity Sought</p>
            <p className="text-sm font-data font-semibold text-foreground">{opportunity?.equitySought}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground font-body line-clamp-2 mb-3 flex-1">
          {opportunity?.description}
        </p>

        {/* Impact themes */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {opportunity?.impactThemes?.slice(0, 3)?.map((theme) => (
            <span
              key={theme}
              className="px-2 py-0.5 rounded-full text-xs font-caption"
              style={{ background: "rgba(59,76,76,0.12)", color: "#92600A" }}
            >
              {theme}
            </span>
          ))}
        </div>

        {/* Actions — role aware */}
        {isExternal && mktCfg ? (
          // External investor: progressive disclosure CTA
          <div className="flex gap-2 mt-auto">
            <Button
              variant="default"
              fullWidth
              iconName={mktCfg.ctaAction === 'request_access' ? 'Send' : 'ArrowRight'}
              iconPosition="right"
              iconSize={15}
              disabled={!mktCfg.ctaAction}
              onClick={() => {
                if (mktCfg.ctaAction === 'request_access') {
                  onRequestAccess?.(opportunity.id);
                } else if (mktCfg.ctaAction) {
                  navigate(ROUTES.dealRoom);
                }
              }}
            >
              {mktCfg.cta}
            </Button>
          </div>
        ) : (
          // Internal: standard deal team actions
          <div className="flex gap-2 mt-auto">
            <Button
              variant="default"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              iconSize={15}
              onClick={() => navigate(ROUTES.opportunityDetail.replace(':id', opportunity?.id))}
            >
              View Details
            </Button>
            <Button
              variant="outline"
              iconName="Plus"
              iconSize={15}
              onClick={() => navigate("/spv-creation-wizard")}
              title="Create SPV"
            >
              SPV
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}